/**
 * Centralised, lazy Bugsnag.
 *
 * Keeps @bugsnag/js + @bugsnag/plugin-vue + @bugsnag/browser-performance
 * (~98KB minified) OUT of the eager app bundle that gates first interaction.
 * Bugsnag is prod-only observability, so a short delay before it initialises is an
 * acceptable trade for faster interactivity. Reports raised before Bugsnag has
 * finished loading are buffered and flushed on init, so nothing is lost.
 *
 * Usage:
 *   import { notifyBugsnag, startBugsnag } from '@/utils/bugsnag'
 *   notifyBugsnag(error)                  // fire-and-forget
 *   notifyBugsnag(error, (event) => {})   // with the per-event onError callback
 *   startBugsnag()                        // proactively warm it (called on idle)
 */
import { limitBreadcrumb, limitEventPayload } from './bugsnagPayload'

const enabled = !!import.meta.env.VITE_BUGSNAG_API_KEY
const appVersion = import.meta.env.VITE_BUGSNAG_APP_VERSION

let instance = null
let starting = null
const buffer = [] // { error, onError }

// Global event enrichment — moved verbatim from app.js's Bugsnag.start({ onError }).
function enrichEvent(event) {
    // Add navigation context
    event.addMetadata('navigation', {
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer || null,
        userAgent: navigator.userAgent,
        online: navigator.onLine,
        visibility: document.visibilityState,
    })

    // Enrich Axios errors with request/response details
    const originalError = event.originalError
    if (['AbortError', 'NotAllowedError', 'NotSupportedError'].includes(event.errors?.[0]?.errorClass)) {
        event.addMetadata('playback', {
            attempt: originalError?.scenePlayback || null,
            userActivation: navigator.userActivation ? {
                isActive: navigator.userActivation.isActive,
                hasBeenActive: navigator.userActivation.hasBeenActive,
            } : null,
            // State only: signed media URLs and ad URLs must not enter telemetry.
            media: Array.from(document.querySelectorAll('video, audio')).slice(0, 6).map((media) => ({
                player: media.closest('dl8-video') ? 'delight'
                    : media.closest('.ima-ad-container') ? 'ima-ad'
                    : media.closest('.video-js') ? 'videojs' : media.tagName.toLowerCase(),
                paused: media.paused,
                muted: media.muted,
                autoplay: media.autoplay,
                readyState: media.readyState,
                networkState: media.networkState,
                errorCode: media.error?.code || null,
                source: media.error ? mediaSourceKind(media) : null,
            })),
        })
    }
    if (originalError?.isAxiosError) {
        const { config, response, message } = originalError
        event.addMetadata('axios_request', {
            method: config?.method?.toUpperCase(),
            url: config?.url,
            baseURL: config?.baseURL,
            timeout: config?.timeout,
            params: config?.params,
        })
        if (response) {
            const body = typeof response.data === 'string'
                ? response.data.substring(0, 1000)
                : JSON.stringify(response.data)?.substring(0, 1000) || null
            event.addMetadata('axios_response', {
                status: response.status,
                statusText: response.statusText,
                body: body,
            })
        } else {
            event.addMetadata('axios_response', {
                message: message,
                code: originalError.code,
            })
        }
    }
}

/**
 * Media rejections raised by normal player operation. Returning false from onError
 * discards only these:
 * - a pause or source change interrupting a pending play();
 * - a seek or source change aborting the media fetch (MEDIA_ERR_ABORTED's message);
 * - WebKit's wording for the same interrupted play(): a stackless "The operation was
 *   aborted." Firefox uses that text for an aborted fetch(), so it is only dropped on WebKit;
 * - an autoplay attempt the browser blocked before the visitor ever interacted with
 *   the page. A scene play that playSceneMedia tagged, or any play after a gesture,
 *   is still reported. *
 * Plus the browser's ResizeObserver loop warning (both wordings): an observer callback
 * resized something and the rest was delivered next frame. Harmless by spec, never ours
 * to fix (~500 events in 3 days, NEX-9408).
 */
/** Safari and every iOS browser; Chrome and Edge also send "AppleWebKit" but are Blink. */
function isWebKit() {
    const userAgent = navigator.userAgent || ''
    return /AppleWebKit/.test(userAgent) && !/Chrome\/|Chromium\/|Edg\//.test(userAgent)
}

function isNoise(event) {
    const error = event.errors?.[0]
    const message = error?.errorMessage || ''
    if (/^ResizeObserver loop (completed with undelivered notifications|limit exceeded)/.test(message)) {
        return true
    }
    if (error?.errorClass === 'AbortError') {
        if (message === 'The operation was aborted.' && !error.stacktrace?.length) {
            return isWebKit()
        }
        return /play\(\) request was interrupted|fetching process for the media resource was aborted/.test(message)
    }
    if (error?.errorClass === 'NotAllowedError' && !error.stacktrace?.length) {
        return !event.originalError?.scenePlayback
            && navigator.userActivation?.hasBeenActive === false
    }
    return false
}

/**
 * Coarse kind of a media element's source: never the URL itself, which is signed. Lets
 * one NotSupportedError group per cause form (NEX-9408).
 */
function mediaSourceKind(media) {
    const src = media.currentSrc || media.getAttribute('src') || ''
    if (!src) {
        return 'no-src'
    }
    if (src.startsWith('blob:')) {
        return 'mse'
    }
    if (/\.m3u8(\?|$)/.test(src)) {
        return 'hls'
    }
    if (/_4k\.mp4(\?|$)/i.test(src)) {
        return '4k-mp4'
    }
    if (/\.mp4(\?|$)/.test(src)) {
        return 'mp4'
    }
    return 'other'
}

/** Source kind of the first media element in an error state, or 'none'. */
function failedMediaSource() {
    const failed = Array.from(document.querySelectorAll('video, audio')).find((media) => media.error)
    return failed ? mediaSourceKind(failed) : 'none'
}

/**
 * Stackless media rejections all group under one hash per class. NotSupportedError mixed
 * at least five causes that way (expired links, 4K switches, broken VODs, ad recovery…),
 * so it is split further by the tagged playback action and the failed source's kind.
 */
function stacklessGroupingHash(event, errorClass) {
    if (errorClass !== 'NotSupportedError') {
        return `stackless:${errorClass}`
    }
    const action = event.originalError?.scenePlayback?.action || 'untagged'
    return `stackless:NotSupportedError:${action}:${failedMediaSource()}`
}

function onError(event) {
    if (isNoise(event)) {
        return false
    }
    const error = event.errors?.[0]
    if (!event.groupingHash && error && !error.stacktrace?.length) {
        if (['AbortError', 'NotAllowedError', 'NotSupportedError'].includes(error.errorClass)) {
            event.groupingHash = stacklessGroupingHash(event, error.errorClass)
        } else if (error.errorMessage === 'Invalid call to runtime.sendMessage(). Tab not found.') {
            event.groupingHash = 'extension:runtime-sendMessage-tab-not-found'
        }
    }
    enrichEvent(event)
    limitEventPayload(event)
}

/**
 * Dynamically import + start Bugsnag (once). Returns a promise resolving to the
 * Bugsnag instance, or null when disabled / on failure.
 */
export function startBugsnag() {
    if (!enabled) {
        return Promise.resolve(null)
    }
    if (!starting) {
        starting = Promise.all([
            import('@bugsnag/js'),
            import('@bugsnag/plugin-vue'),
        ])
            .then(([{ default: Bugsnag }, { default: BugsnagPluginVue }]) => {
                Bugsnag.start({
                    apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
                    plugins: [new BugsnagPluginVue()],
                    enabledReleaseStages: ['production'],
                    releaseStage: import.meta.env.VITE_APP_ENV,
                    appVersion,
                    onBreadcrumb: limitBreadcrumb,
                    onError,
                })
                instance = Bugsnag

                // Flush anything reported before init.
                buffer.splice(0).forEach(({ error, onError }) => {
                    try {
                        onError ? Bugsnag.notify(error, onError) : Bugsnag.notify(error)
                    } catch (e) {
                        // swallow — never let telemetry throw
                    }
                })

                // Performance monitoring is the lowest priority — load it last.
                import('@bugsnag/browser-performance')
                    .then(({ default: BugsnagPerformance }) => {
                        BugsnagPerformance.start({ apiKey: import.meta.env.VITE_BUGSNAG_API_KEY, appVersion })
                    })
                    .catch(() => {})

                return Bugsnag
            })
            .catch(() => null)
    }
    return starting
}

/**
 * Leave a breadcrumb on the next reported error. Dropped when Bugsnag hasn't started yet —
 * breadcrumbs are context, not worth buffering or triggering a load for.
 *
 * @param {string} message
 * @param {Record<string, unknown>} [metadata]
 */
export function leaveBugsnagBreadcrumb(message, metadata) {
    if (!enabled || !instance) {
        return
    }
    try {
        instance.leaveBreadcrumb(message, metadata, 'log')
    } catch (e) {
        // swallow
    }
}

/**
 * Report an error. Buffers (and triggers a load) if Bugsnag isn't ready yet.
 * Accepts the optional per-event onError callback that Bugsnag.notify supports.
 */
export function notifyBugsnag(error, onError) {
    if (!enabled) {
        return
    }
    const prepareEvent = (event) => {
        const result = onError?.(event)
        limitEventPayload(event)
        return result
    }
    if (instance) {
        try {
            instance.notify(error, prepareEvent)
        } catch (e) {
            // swallow
        }
    } else {
        buffer.push({ error, onError: prepareEvent })
        startBugsnag()
    }
}
